import React, { useState, useEffect } from 'react';
import { SAPLayout } from '@/layouts/SAPLayout/SAPLayout';
import { mockSapSimulatorService } from '@/services/mock/MockSapSimulatorService';
import { SapPaymentRecord } from '@/types/domain/sap-simulator';
import { DataTable, ColumnDef } from '@/components/data-display/DataTable';
import { formatCurrency } from '@/lib/utils';
import { CreditCard } from 'lucide-react';

export const SapPaymentsPage: React.FC = () => {
  const [payments, setPayments] = useState<SapPaymentRecord[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadPayments = async () => {
      try {
        setIsLoading(true);
        const data = await mockSapSimulatorService.getPayments();
        setPayments(data.data);
      } finally {
        setIsLoading(false);
      }
    };
    loadPayments();
  }, []);

  const columns: ColumnDef<SapPaymentRecord>[] = [
    {
      header: 'Payment Clearing ID',
      accessorKey: 'paymentId',
      cell: (row) => <span className="font-mono font-extrabold text-primary text-xs">{row.paymentId}</span>,
    },
    {
      header: 'Invoice Reference',
      accessorKey: 'invoiceNumber',
      cell: (row) => <span className="font-mono font-bold text-purple-600 text-xs">{row.invoiceNumber}</span>,
    },
    {
      header: 'Vendor Name',
      cell: (row) => (
        <div className="text-xs">
          <span className="font-bold text-foreground">{row.vendorName}</span>
          <p className="text-[11px] font-mono text-foreground-muted">{row.vendorCode}</p>
        </div>
      ),
    },
    {
      header: 'Clearing Amount',
      accessorKey: 'amount',
      cell: (row) => <span className="font-extrabold text-xs text-primary">{formatCurrency(row.amount, row.currency)}</span>,
    },
    {
      header: 'Due Date',
      accessorKey: 'dueDate',
      cell: (row) => <span className="font-mono text-xs text-foreground-secondary">{row.dueDate}</span>,
    },
    {
      header: 'Status',
      accessorKey: 'status',
      cell: (row) => (
        <span className="text-[10px] font-extrabold uppercase bg-emerald-500/10 text-emerald-700 px-2 py-0.5 rounded-full">
          {row.status}
        </span>
      ),
    },
  ];

  return (
    <SAPLayout>
      <div className="space-y-6 font-heading">
        <div>
          <h2 className="text-xl font-extrabold text-foreground flex items-center gap-2">
            <CreditCard className="h-5 w-5 text-primary" /> SAP Payment Clearing (F110)
          </h2>
          <p className="text-xs text-foreground-secondary font-medium">
            Simulated F110 automatic payment run clearing verified vendor invoices.
          </p>
        </div>

        <DataTable data={payments} columns={columns} isLoading={isLoading} />
      </div>
    </SAPLayout>
  );
};
